import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './css/StatusReport.css';
import API_BASE_URL from './config';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const ProductResponseStatus = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { productName } = location.state || {};
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    if (!productName) {
      navigate('/product'); // Redirect if no productName is provided
      return;
    }

    const fetchCustomers = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/customers/by-product/${productName}`, {
          withCredentials: true,
        });
        setCustomers(response.data.data);
      } catch (error) {
        console.error('Error fetching customers:', error);
        alert('Failed to fetch response data. Please try again.');
      }
    };

    fetchCustomers();
  }, [productName, navigate]);

  const getRowColor = (customer) => {
    if (customer.completeness >= 60) return 'green-row';
    if (customer.completeness >= 45) return 'yellow-row';
    return 'red-row';
  };

  const getResponseStatus = (customer) => {
    if (customer.completeness >= 60) return 'Ready';
    if (customer.completeness >= 45) return 'Close';
    return 'Not Ready';
  };

  const calculateParticipantsByRelationship = (participants, relationships) => {
    return participants.filter((participant) =>
      relationships.includes(participant.relationship.toLowerCase()) && participant.completed
    ).length;
  };

  const exportToPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Rater Response Status Report', 20, 20);

    const legendYStart = 30;
    const legendItems = [
      { color: [212, 237, 218], label: 'Ready' },
      { color: [255, 243, 205], label: 'Close' },
      { color: [248, 215, 218], label: 'Not Ready' },
    ];

    let currentY = legendYStart;
    legendItems.forEach((item) => {
      doc.setFillColor(...item.color);
      doc.rect(20, currentY - 5, 5, 5, 'F');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.text(item.label, 30, currentY);
      currentY += 7;
    });

    const tableColumn = ['Leader Name', 'Email', 'Response Status', 'Total Raters', 'Responses', 'Other', 'Mgr', 'Self'];
    const tableRows = customers.map((customer) => {
      const totalOther = calculateParticipantsByRelationship(customer.participants, ['other', 'direct report', 'peer']);
      const totalMgr = calculateParticipantsByRelationship(customer.participants, ['manager']);
      const totalSelf = calculateParticipantsByRelationship(customer.participants, ['self']);

      return {
        row: [
          `${customer.firstName} ${customer.lastName}`,
          customer.email,
          getResponseStatus(customer),
          customer.totalParticipants,
          customer.completedParticipants,
          totalOther,
          totalMgr,
          totalSelf,
        ],
        color: getRowColor(customer),
      };
    });

    doc.autoTable({
      head: [tableColumn],
      body: tableRows.map((item) => item.row),
      startY: currentY + 10,
      styles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontSize: 10 },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
      didParseCell: (data) => {
        const rowColor = tableRows[data.row.index]?.color;
        if (data.section === 'body' && rowColor) {
          if (rowColor === 'red-row') data.cell.styles.fillColor = [248, 215, 218];
          else if (rowColor === 'yellow-row') data.cell.styles.fillColor = [255, 243, 205];
          else if (rowColor === 'green-row') data.cell.styles.fillColor = [212, 237, 218];
          data.cell.styles.textColor = [0, 0, 0];
        }
      },
    });

    doc.save(`${productName}_Response_Status_Report.pdf`);
  };

  return (
    <div className="status-report-container">
      <h2>Rater Response Status - {productName || 'Unknown'}</h2>
      <button onClick={exportToPdf} className="export-pdf-button">Export to PDF</button>
      <button className="back-button" onClick={() => navigate('/product')}>
        Back to Product Page
      </button>
      <div className="legend">
        <span className="legend-item">
          <span className="legend-color green-box"></span> Ready
        </span>
        <span className="legend-item">
          <span className="legend-color yellow-box"></span> Close
        </span>
        <span className="legend-item">
          <span className="legend-color red-box"></span> Not Ready
        </span>
      </div>
      <table className="status-report-table">
        <thead>
          <tr>
            <th>Leader Name</th>
            <th>Email</th>
            <th>Response Status</th>
            <th>Total Raters</th>
            <th>Responses</th>
            <th>Other</th>
            <th>Mgr</th>
            <th>Self</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((customer) => {
            const totalOther = calculateParticipantsByRelationship(customer.participants, ['other', 'direct report', 'peer']);
            const totalMgr = calculateParticipantsByRelationship(customer.participants, ['manager']);
            const totalSelf = calculateParticipantsByRelationship(customer.participants, ['self']);

            return (
              <tr key={customer._id} className={getRowColor(customer)}>
                <td>{customer.firstName} {customer.lastName}</td>
                <td>{customer.email}</td>
                <td>{getResponseStatus(customer)}</td>
                <td>{customer.totalParticipants}</td>
                <td>{customer.completedParticipants}</td>
                <td>{totalOther}</td>
                <td>{totalMgr}</td>
                <td>{totalSelf}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ProductResponseStatus;